/**
 * Faixa de pontuação de um perfil associado ao questionário, como aparece na seção
 * "Associe o perfil".
 */
export interface FaixaPerfil {
  idPerfilInvestimento: number;
  nomePerfil: string;
  pontuacaoMinima: number | null;
  pontuacaoMaxima: number | null;
}

/** Campo de uma faixa que o problema pede para corrigir — a tela o destaca. */
export interface CampoFaixa {
  idPerfilInvestimento: number;
  campo: 'minima' | 'maxima';
}

export interface ProblemaCobertura {
  mensagem: string;
  campos: CampoFaixa[];
}

/** Pontuação que o participante pode somar respondendo o questionário inteiro. */
export interface PontuacaoAtingivel {
  minima: number;
  maxima: number;
}

interface PerguntaPontuada {
  alternativas: { pontos: number | null }[];
}

/**
 * Soma, pergunta a pergunta, a menor e a maior pontuação disponível — o piso e o teto que o
 * participante pode atingir. Devolve `null` enquanto alguma pergunta não tiver pontuação
 * definida: sem isso o intervalo seria menor que o real e cobraria dos perfis uma faixa errada.
 */
export function calcularPontuacaoAtingivel(
  perguntas: readonly PerguntaPontuada[],
): PontuacaoAtingivel | null {
  if (perguntas.length === 0) {
    return null;
  }

  let minima = 0;
  let maxima = 0;

  for (const pergunta of perguntas) {
    const pontos = pergunta.alternativas
      .map((alternativa) => alternativa.pontos)
      .filter((valor): valor is number => valor != null && Number.isFinite(valor));

    if (pontos.length === 0) {
      return null;
    }

    minima += Math.min(...pontos);
    maxima += Math.max(...pontos);
  }

  return { minima, maxima };
}

/**
 * Confere se os perfis cobrem, sem buraco nem sobreposição, todo o intervalo que o participante
 * pode atingir. Sem essa cobertura, uma pontuação cai fora de qualquer faixa e o participante
 * termina o questionário sem perfil.
 *
 * Devolve todas as inconsistências encontradas, para a pessoa corrigi-las de uma vez, ou uma
 * lista vazia quando a cobertura está correta. Faixas ainda em branco não são cobradas aqui —
 * quem faz isso é o `required` de cada campo.
 */
export function validarCoberturaDePerfis(
  faixas: readonly FaixaPerfil[],
  atingivel: PontuacaoAtingivel,
): ProblemaCobertura[] {
  const preenchidas = faixas.filter(
    (faixa) => faixa.pontuacaoMinima != null && faixa.pontuacaoMaxima != null,
  );

  if (preenchidas.length === 0 || preenchidas.length !== faixas.length) {
    return [];
  }

  const invertidas = preenchidas.filter(
    (faixa) => (faixa.pontuacaoMinima as number) > (faixa.pontuacaoMaxima as number),
  );

  // Uma faixa invertida desordena a sequência inteira: as mensagens de buraco e sobreposição
  // sairiam apontando pares que só existem por causa dela. Primeiro a faixa se endireita.
  if (invertidas.length > 0) {
    return invertidas.map((faixa) => ({
      mensagem: `No perfil ${faixa.nomePerfil}, a pontuação mínima não pode ser maior que a máxima.`,
      campos: [
        { idPerfilInvestimento: faixa.idPerfilInvestimento, campo: 'minima' as const },
        { idPerfilInvestimento: faixa.idPerfilInvestimento, campo: 'maxima' as const },
      ],
    }));
  }

  const ordenadas = [...preenchidas].sort(
    (a, b) => (a.pontuacaoMinima as number) - (b.pontuacaoMinima as number),
  );
  const problemas: ProblemaCobertura[] = [];

  const primeira = ordenadas[0];
  if ((primeira.pontuacaoMinima as number) !== atingivel.minima) {
    problemas.push({
      mensagem: `A menor pontuação dos perfis deve ser ${atingivel.minima}, que é o mínimo que o participante pode atingir.`,
      campos: [{ idPerfilInvestimento: primeira.idPerfilInvestimento, campo: 'minima' }],
    });
  }

  // O teto é o maior fim entre todas as faixas, não o da última da ordenação: uma faixa curta
  // listada por último não significa que ninguém alcança o máximo.
  const maisAlta = ordenadas.reduce((maior, faixa) =>
    (faixa.pontuacaoMaxima as number) > (maior.pontuacaoMaxima as number) ? faixa : maior,
  );
  if ((maisAlta.pontuacaoMaxima as number) !== atingivel.maxima) {
    problemas.push({
      mensagem: `A maior pontuação dos perfis deve ser ${atingivel.maxima}, que é o máximo que o participante pode atingir.`,
      campos: [{ idPerfilInvestimento: maisAlta.idPerfilInvestimento, campo: 'maxima' }],
    });
  }

  // A referência é o ponto mais alto já coberto, e não o fim da faixa anterior: com faixas que
  // se sobrepõem, olhar só a vizinha faria uma pontuação já coberta parecer um buraco.
  let cobertura = ordenadas[0];

  for (let i = 1; i < ordenadas.length; i += 1) {
    const atual = ordenadas[i];
    const esperado = (cobertura.pontuacaoMaxima as number) + 1;
    const inicio = atual.pontuacaoMinima as number;
    const fim = atual.pontuacaoMaxima as number;
    // Os dois lados do encontro entram no destaque: o ajuste pode ser em qualquer um deles.
    const campos: CampoFaixa[] = [
      { idPerfilInvestimento: cobertura.idPerfilInvestimento, campo: 'maxima' },
      { idPerfilInvestimento: atual.idPerfilInvestimento, campo: 'minima' },
    ];

    if (inicio > esperado) {
      problemas.push({
        mensagem: `Nenhum perfil cobre a pontuação entre ${esperado} e ${inicio - 1}. Ajuste as faixas de ${cobertura.nomePerfil} e ${atual.nomePerfil}.`,
        campos,
      });
    } else if (inicio < esperado) {
      problemas.push({
        mensagem: `As faixas de ${cobertura.nomePerfil} e ${atual.nomePerfil} se sobrepõem: ${atual.nomePerfil} deve começar em ${esperado}.`,
        campos,
      });
    }

    if (fim > (cobertura.pontuacaoMaxima as number)) {
      cobertura = atual;
    }
  }

  return problemas;
}
